import { Field } from 'formik'
import {
  FC,
  useState,
  useRef,
  useEffect,
  ChangeEvent,
  Fragment,
  ReactElement,
} from 'react'
import { StyleFormWrapper, StyleLabel, StylePlaceholder } from '../style'
import {
  StyleDropDownWrapper,
  StyleDropDownOptionWrapper,
  StyleDropDownOption,
  StyleSelectedValue,
  StyleDropDownSearchInput,
  StyleDropDownSearchWrapper,
  StyleDropDownOptionItems,
  StyleDropDownIconWrapper,
  StyleSelectedItems,
  StyleSelectedItem,
} from './style'
import ErrorMessage from '../ErrorMessage'
import Svg from '@components/svg'
import debounce from '@utils/debounce'
import { ChevronDown } from 'lucide-react'
interface DropDownProps {
  options: Array<any>
  onChange?: (value: any) => void
  onSearch?: (value: any) => void
  label?: string
  placeholder?: string
  valueAsKey: string
  titleAsKey: string
  name: string
  isMultiple?: boolean
  isServerside?: boolean
  hasSearch?: boolean
  styleWrapper?: string
  styleLabel?: string
  styleInput?: string
  styleOptionsWrapper?: string
  disabled?: boolean
  tc?: any
  rightComponent?: ReactElement
}
const DropDown: FC<DropDownProps> = ({
  options,
  label,
  onChange,
  onSearch,
  placeholder,
  valueAsKey,
  titleAsKey,
  isServerside = false,
  hasSearch = false,
  name,
  styleInput = '',
  styleLabel = '',
  styleWrapper = '',
  styleOptionsWrapper = '',
  disabled,
  tc,
  isMultiple = false,
  rightComponent,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const handleChange = ({ form, value }) => {
    setIsOpen(false)
    if (onChange) {
      onChange(value)
    } else {
      form.setFieldValue(name, value)
    }
  }

  const handleMultiple = ({ form, value, values }) => {
    const findIndex = values?.findIndex((selected: any) => selected == value)
    if (findIndex >= 0) {
      const filterValues = values?.filter((item) => item != value)
      if (!!onChange) {
        onChange(filterValues)
      } else {
        form.setFieldValue(name, filterValues)
      }
    } else {
      const finalValues = [...values, value]
      if (onChange) {
        onChange(finalValues)
      } else {
        form.setFieldValue(name, finalValues)
      }
    }
  }

  const handleSearch = debounce((value: string) => {
    if (!isServerside) {
      setSearch(value)
    }
    onSearch && onSearch(value)
  }, 600)

  const onClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', onClickOutside)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
    }
  }, [])
  return (
    <Field name={name}>
      {({ field, meta, form }) => {
        const hasError = meta.touched && meta.error

        return (
          <StyleFormWrapper styleWrapper={styleWrapper}>
            {!!label && (
              <StyleLabel hasError={hasError} styleLabel={styleLabel}>
                {label}
              </StyleLabel>
            )}
            <StyleDropDownWrapper
              hasError={hasError}
              onClick={() => {
                !disabled && setIsOpen(!isOpen)
              }}
              disabled={disabled}
              styleInput={styleInput}
            >
              {!!placeholder &&
              (!!isMultiple ? !field?.value?.length : !field?.value) ? (
                <StylePlaceholder>{placeholder}</StylePlaceholder>
              ) : (
                <>
                  {!!isMultiple ? (
                    <StyleSelectedItems>
                      {options
                        ?.filter((option) => {
                          return field.value
                            ?.map(String)
                            ?.includes(String(option[valueAsKey]))
                        })
                        ?.map((item) => {
                          return (
                            <Fragment key={item?.id}>
                              <StyleSelectedItem>
                                {item?.[titleAsKey]}
                              </StyleSelectedItem>
                            </Fragment>
                          )
                        })}
                    </StyleSelectedItems>
                  ) : (
                    <StyleSelectedValue tc={tc}>
                      {
                        options?.find(
                          (option: any) => option[valueAsKey] == field?.value,
                        )?.[titleAsKey]
                      }
                    </StyleSelectedValue>
                  )}
                </>
              )}

              <StyleDropDownIconWrapper>
                {!!rightComponent && rightComponent}
                <ChevronDown />
                {/* <Svg svg="chevronDown" size={12} color={'text200'} /> */}
              </StyleDropDownIconWrapper>
            </StyleDropDownWrapper>
            {!!isOpen && (
              <StyleDropDownOptionWrapper
                styleOptionsWrapper={styleOptionsWrapper}
                ref={dropdownRef}
                top={!!label ? 80 : 50}
              >
                {!!hasSearch && (
                  <StyleDropDownSearchWrapper>
                    <StyleDropDownSearchInput
                      type="text"
                      placeholder="Search"
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        handleSearch(event.target.value)
                      }
                    />
                  </StyleDropDownSearchWrapper>
                )}
                <StyleDropDownOptionItems
                  onClick={(event: any) => {
                    const value = (event.target as HTMLElement)
                      .closest('[data-id]')
                      ?.getAttribute('data-id')
                    if (!!value) {
                      if (!!isMultiple) {
                        handleMultiple({
                          form,
                          value,
                          values: field?.value,
                        })
                      } else {
                        handleChange({
                          form,
                          value,
                        })
                      }
                      setIsOpen(false)
                    }
                  }}
                >
                  {options
                    ?.filter((option) =>
                      option[titleAsKey]
                        ?.toLowerCase()
                        ?.includes(search.toLowerCase()),
                    )
                    .map((option) => (
                      <Fragment key={option[valueAsKey]}>
                        <StyleDropDownOption
                          data-id={option[valueAsKey]}
                          isActive={
                            !!isMultiple
                              ? field.value
                                  ?.map(String)
                                  ?.includes(String(option[valueAsKey]))
                              : field.value == option[valueAsKey]
                          }
                        >
                          {option[titleAsKey]}
                        </StyleDropDownOption>
                      </Fragment>
                    ))}
                </StyleDropDownOptionItems>
              </StyleDropDownOptionWrapper>
            )}

            {!!hasError && <ErrorMessage message={meta.error} />}
          </StyleFormWrapper>
        )
      }}
    </Field>
  )
}

export default DropDown
